/**
 * Die Grundkonfiguration eines Mandanten — die Firma hinter den Belegen.
 *
 * Es gibt sie, weil dieselben Angaben an vier Stellen gebraucht werden und dort bisher jeweils
 * neu erfunden wurden: der Briefbogen zeigt sie, die XRechnung trägt sie als Verkäufer, der
 * DATEV-Export braucht das Mandat, und die Belegtexte hängen an den Konditionen. Eine Quelle,
 * damit Rechnung und Briefbogen nicht zwei verschiedene Firmen behaupten können.
 */

export interface CompanyAddress {
  readonly street: string;
  readonly postalCode: string;
  readonly city: string;
  /** ISO-3166 alpha-2, z. B. DE. */
  readonly countryCode: string;
}

/** Wer die Firma rechtlich ist — Pflichtangaben nach § 35a GmbHG landen in der Fußzeile. */
export interface CompanyIdentity {
  readonly name: string;
  readonly legalForm: string;
  readonly address: CompanyAddress;
  /** Handelsregisternummer, z. B. HRB 1234. */
  readonly register: string;
  readonly registerCourt: string;
  /** Geschäftsführer / Inhaber — auf Geschäftsbriefen Pflicht. */
  readonly managingDirectors: string;
  readonly vatId: string;
  readonly taxNumber: string;
}

export interface CompanyContact {
  readonly phone: string;
  readonly fax: string;
  readonly email: string;
  readonly website: string;
  readonly contactName: string;
  readonly contactPhone: string;
  readonly contactEmail: string;
}

export interface CompanyBank {
  readonly bankName: string;
  readonly iban: string;
  readonly bic: string;
}

/**
 * Der Briefbogen als DATEN, nicht als hochgeladenes PDF. Damit trägt dieselbe Definition
 * Bildschirm, Druck und später die PDF-Ausgabe — ein hinterlegtes Hintergrund-PDF könnte das
 * nicht, und niemand müsste HTML können, um es zu ändern.
 */
export interface Letterhead {
  /**
   * Der Briefkopf besteht aus drei frei belegbaren Bild-Slots (data:-URI, damit ohne externen Abruf
   * druckbar): links / Mitte / rechts. So kann z. B. das Logo links und eine Leistungsgrafik rechts
   * stehen. logoDataUrl ist der linke Slot (Name aus Kompatibilität mit dem früheren Einzel-Logo).
   */
  readonly logoDataUrl: string;
  readonly logoCenterDataUrl: string;
  readonly logoRightDataUrl: string;
  readonly accentColor: string;
  /** Die kleine Absenderzeile über dem Anschriftfeld (DIN 5008). */
  readonly senderLine: string;
  /** Fußzeile, spaltenweise — je Spalte eine Zeile je Zeilenumbruch. */
  readonly footerColumns: readonly string[];
  /** Falzmarken und Lochmarke andrucken (DIN 5008). */
  readonly showFoldMarks: boolean;
  /** Oberer Rand in mm bis zum Briefkopf. */
  readonly marginTopMm: number;
  readonly marginLeftMm: number;
  readonly marginRightMm: number;
  readonly marginBottomMm: number;
}

/**
 * Ein Block im Beleg-Layout (Block-Layout-Editor): eine Block-Id plus Sichtbarkeit; die Reihenfolge
 * ergibt sich aus der Array-Position. Die Id ist bewusst ein loser String — WELCHE Blöcke es je
 * Belegart gibt, weiß der Renderer (orderBlockCatalog in @forq/orders/documents), nicht das Datenmodell.
 */
export interface DocumentBlockPlacement {
  readonly id: string;
  readonly hidden?: boolean;
}

/**
 * Eine frei gestaltete Belegvorlage aus dem Report-Designer (GrapesJS). `html`/`css` ist das gerenderte
 * Ergebnis (mit Platzhaltern wie {{kunde.name}} und Wiederhol-Bereichen für die Positionen), `project`
 * der Designer-Zustand zum erneuten Öffnen. Ist für eine Belegart eine Vorlage hinterlegt, rendert der
 * Beleg über sie statt über das eingebaute Layout; sonst gilt weiter das Standard-Layout.
 */
export interface ReportTemplate {
  readonly html: string;
  readonly css: string;
  /** Undurchsichtiger Designer-Zustand (GrapesJS-Projekt-JSON) zum Wiederöffnen. */
  readonly project?: unknown;
}

/** Vorbelegungen für Belege — was ohne Zutun in einem Angebot oder einer Rechnung steht. */
export interface DocumentDefaults {
  readonly paymentTermDays: number;
  readonly cashDiscountPercent: number;
  readonly cashDiscountDays: number;
  readonly quoteValidityDays: number;
  readonly quoteFooterText: string;
  readonly invoiceFooterText: string;
  readonly deliveryNoteFooterText: string;
  /**
   * Anrede/Einleitung je Belegart (Vorlagen-Editor). Frei editierbar; Platzhalter {titel} {kunde}
   * {nummer} werden beim Rendern durch die Vorgangswerte ersetzt. Leer = die eingebaute Standard-Anrede.
   */
  readonly offerIntro: string;
  readonly confirmationIntro: string;
  readonly deliveryNoteIntro: string;
  readonly workshopOrderIntro: string;
  /** Rechnung: Einleitung + Zahlungssatz. Platzhalter {kunde} {nummer} {fällig}. Leer = Standardtext. */
  readonly invoiceIntro: string;
  readonly invoicePaymentText: string;
  /**
   * Block-Layout je Belegart (Block-Layout-Editor): Reihenfolge + Sichtbarkeit der Belegblöcke, je
   * Belegart-Schlüssel (z. B. 'offer', 'confirmation', 'delivery-note', 'workshop-order'). Fehlt ein
   * Schlüssel oder ist die Liste leer, gilt die eingebaute Standard-Reihenfolge. Whole-Blob-jsonb —
   * neue Belegarten/Blöcke brauchen keine Migration.
   */
  readonly blockLayouts: Record<string, ReadonlyArray<DocumentBlockPlacement>>;
  /**
   * Frei gestaltete Belegvorlagen je Belegart-Schlüssel (Report-Designer). Ist ein Grundbriefbogen
   * (reportBase) gesetzt, ist dies der INHALT je Belegart (kommt in den Slot des Grundbriefbogens);
   * sonst die eigenständige Gesamtvorlage. Fehlt ein Schlüssel, gilt das eingebaute Layout bzw. der
   * eingebaute Inhalt. Whole-Blob-jsonb — keine Migration.
   */
  readonly reportTemplates: Record<string, ReportTemplate>;
  /**
   * Der Grundbriefbogen: ein geteilter Rahmen (Briefkopf, Fußzeile, Anschrift/Meta) mit dem Platzhalter
   * {{beleg_inhalt}} an der Stelle, wo der belegtypische Inhalt erscheint. Ist er gesetzt, rendern ALLE
   * Belegarten über ihn — eine Änderung schlägt auf alle durch. Fehlt er, gilt das bisherige Verhalten.
   */
  readonly reportBase?: ReportTemplate;
}

/**
 * Eine Mahnstufe. Text, Gebühr und Frist gehören dem Mandanten -- was FTW schreibt und verlangt,
 * ist eine kaufmännische Entscheidung und keine Programmkonstante.
 */
export interface DunningLevelSettings {
  readonly label: string;
  /** Mahngebühr in EUR. 0 = keine. */
  readonly fee: number;
  /** Neue Zahlungsfrist in Tagen ab dem Mahndatum. */
  readonly dueDays: number;
  /** Der Absatz im Mahnbrief. Platzhalter {nummer} {betrag} {fällig}. */
  readonly text: string;
}

/**
 * Mahnwesen je Mandant.
 *
 * Der Basiszinssatz ändert sich zum 1. Januar und zum 1. Juli jedes Jahres (§ 247 BGB). Ihn im
 * Code zu führen hiesse, ihn zweimal jährlich zu vergessen -- deshalb steht er hier, mit dem Datum,
 * ab dem er gilt, damit man sieht, wie alt der Wert ist.
 */
export interface DunningSettings {
  /** Basiszinssatz in Prozent -- kann negativ sein, das war er jahrelang. */
  readonly baseInterestRate: number;
  /** Ab wann dieser Basiszinssatz gilt (ISO). Leer = nie gepflegt. */
  readonly baseInterestRateFrom: string;
  /** Verzugszinsen überhaupt berechnen? Manche Kundenbeziehung verträgt das nicht. */
  readonly chargeInterest: boolean;
  readonly levels: readonly DunningLevelSettings[];
}

/** Das DATEV-Mandat — kommt vom Steuerberater, wird nie geraten. */
export interface DatevMandate {
  readonly consultantNumber: string;
  readonly clientNumber: string;
  /** Erster Tag des Wirtschaftsjahres, YYYY-MM-DD. */
  readonly fiscalYearStart: string;
  /** Länge der Sachkonten; Personenkonten sind eine Stelle länger. */
  readonly accountLength: number;
}

export interface TenantCompanyProfile {
  readonly identity: CompanyIdentity;
  readonly contact: CompanyContact;
  readonly bank: CompanyBank;
  readonly letterhead: Letterhead;
  readonly documentDefaults: DocumentDefaults;
  readonly datev: DatevMandate;
  /** Mahnstufen, Gebühren und der Basiszinssatz -- siehe DunningSettings. */
  readonly dunning: DunningSettings;
}

export const emptyCompanyProfile: TenantCompanyProfile = {
  identity: {
    name: '', legalForm: '', address: { street: '', postalCode: '', city: '', countryCode: 'DE' },
    register: '', registerCourt: '', managingDirectors: '', vatId: '', taxNumber: '',
  },
  contact: { phone: '', fax: '', email: '', website: '', contactName: '', contactPhone: '', contactEmail: '' },
  bank: { bankName: '', iban: '', bic: '' },
  letterhead: {
    logoDataUrl: '', logoCenterDataUrl: '', logoRightDataUrl: '', accentColor: '#E5232F', senderLine: '', footerColumns: ['', '', ''],
    showFoldMarks: true, marginTopMm: 20, marginLeftMm: 25, marginRightMm: 20, marginBottomMm: 20,
  },
  documentDefaults: {
    paymentTermDays: 14, cashDiscountPercent: 0, cashDiscountDays: 0, quoteValidityDays: 30,
    quoteFooterText: '', invoiceFooterText: '', deliveryNoteFooterText: '',
    offerIntro: 'gerne unterbreiten wir Ihnen zu „{titel}" folgendes Angebot:',
    confirmationIntro: 'wir bestätigen Ihren Auftrag „{titel}" wie folgt:',
    deliveryNoteIntro: 'Lieferung zum Vorgang „{titel}":',
    workshopOrderIntro: 'Arbeitsauftrag „{titel}":',
    invoiceIntro: 'vielen Dank für Ihren Auftrag. Für die nachstehenden Leistungen berechnen wir Ihnen:',
    invoicePaymentText: 'Zahlbar bis {fällig} ohne Abzug auf das unten genannte Konto. Bitte geben Sie bei der Zahlung die Rechnungsnummer {nummer} an.',
    blockLayouts: {},
    reportTemplates: {},
  },
  datev: { consultantNumber: '', clientNumber: '', fiscalYearStart: '', accountLength: 4 },
  dunning: {
    // Kein Basiszinssatz vorbelegt: ein veralteter Wert waere schlimmer als ein leerer, weil er
    // wie eine gepflegte Angabe aussieht. Bis er gesetzt ist, werden keine Zinsen vorgeschlagen.
    baseInterestRate: 0,
    baseInterestRateFrom: '',
    chargeInterest: false,
    levels: [
      {
        label: 'Zahlungserinnerung', fee: 0, dueDays: 7,
        text: 'sicher haben Sie übersehen, dass die unten genannte Rechnung noch offen ist. Bitte gleichen Sie den Betrag bis zum {fällig} aus. Sollten Sie die Zahlung bereits veranlasst haben, betrachten Sie dieses Schreiben als gegenstandslos.',
      },
      {
        label: '1. Mahnung', fee: 5, dueDays: 7,
        text: 'trotz unserer Zahlungserinnerung ist die unten genannte Rechnung weiterhin offen. Wir bitten Sie, den Betrag bis zum {fällig} auszugleichen.',
      },
      {
        label: '2. Mahnung', fee: 10, dueDays: 7,
        text: 'die unten genannte Rechnung ist trotz zweimaliger Aufforderung nicht ausgeglichen. Wir fordern Sie letztmalig auf, den Betrag bis zum {fällig} zu begleichen. Danach behalten wir uns weitere Schritte vor.',
      },
    ],
  },
};

// ---------------------------------------------------------------------------
// Prüfungen — geteilt, weil dieselben Angaben mehrere Ausgaben speisen.
// ---------------------------------------------------------------------------

/** Deutsche USt-IdNr.: DE + 9 Ziffern. Andere EU-Formen weichen ab, daher nur DE streng. */
export function isValidVatId(vatId: string): boolean {
  const value = vatId.replace(/\s/g, '').toUpperCase();
  if (!value) return false;
  if (value.startsWith('DE')) return /^DE\d{9}$/.test(value);
  return /^[A-Z]{2}[0-9A-Z]{2,12}$/.test(value);
}

/** IBAN: Struktur plus Mod-97-Prüfziffer — ein Zahlendreher fällt hier auf, nicht beim Kunden. */
export function isValidIban(iban: string): boolean {
  const value = iban.replace(/\s/g, '').toUpperCase();
  if (!/^[A-Z]{2}\d{2}[0-9A-Z]{10,30}$/.test(value)) return false;
  const rearranged = value.slice(4) + value.slice(0, 4);
  const digits = [...rearranged]
    .map((character) => (/[A-Z]/.test(character) ? String(character.charCodeAt(0) - 55) : character))
    .join('');
  let remainder = 0;
  for (const digit of digits) remainder = (remainder * 10 + Number(digit)) % 97;
  return remainder === 1;
}

export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
}

/**
 * Was die Grundkonfiguration FREISCHALTET. Statt einer Ja/Nein-Vollständigkeit sagt das Profil,
 * welche Ausgabe schon möglich ist — der Briefbogen braucht weniger als eine XRechnung, und ein
 * Anwender soll sehen, was ihm die nächste Eingabe bringt.
 */
export type CompanyCapability = 'letterhead' | 'eInvoice' | 'datevExtf';

export interface CapabilityStatus {
  readonly capability: CompanyCapability;
  readonly label: string;
  readonly ready: boolean;
  readonly issues: readonly string[];
}

function addressIssues(address: CompanyAddress, prefix: string): string[] {
  const issues: string[] = [];
  if (!address.street.trim()) issues.push(`${prefix}Straße fehlt.`);
  if (!address.postalCode.trim()) issues.push(`${prefix}PLZ fehlt.`);
  if (!address.city.trim()) issues.push(`${prefix}Ort fehlt.`);
  if (!/^[A-Z]{2}$/.test(address.countryCode)) issues.push(`${prefix}Ländercode fehlt (z. B. DE).`);
  return issues;
}

/** Reicht für einen Briefbogen: Absender, Anschrift, Kontakt, Pflichtangaben. */
export function letterheadIssues(profile: TenantCompanyProfile): readonly string[] {
  const issues: string[] = [];
  if (!profile.identity.name.trim()) issues.push('Firmenname fehlt.');
  issues.push(...addressIssues(profile.identity.address, ''));
  if (!profile.contact.phone.trim() && !profile.contact.email.trim()) {
    issues.push('Telefon oder E-Mail fehlt – ohne Rückkanal kein Geschäftsbrief.');
  }
  return issues;
}

/** Reicht für eine XRechnung: EN 16931 macht Anschrift, Steuer-ID, Kontakt und IBAN zur Pflicht. */
export function eInvoiceIssues(profile: TenantCompanyProfile): readonly string[] {
  const issues: string[] = [...letterheadIssues(profile)];
  if (!profile.identity.vatId.trim() && !profile.identity.taxNumber.trim()) {
    issues.push('USt-IdNr. oder Steuernummer fehlt – eine der beiden ist Pflicht.');
  } else if (profile.identity.vatId.trim() && !isValidVatId(profile.identity.vatId)) {
    issues.push(`USt-IdNr. „${profile.identity.vatId}" hat kein gültiges Format (z. B. DE123456789).`);
  }
  if (!profile.contact.contactName.trim()) issues.push('Ansprechpartner fehlt (in der XRechnung Pflicht).');
  if (!isValidEmail(profile.contact.contactEmail)) issues.push('E-Mail des Ansprechpartners fehlt oder ist ungültig.');
  if (!profile.contact.contactPhone.trim()) issues.push('Telefon des Ansprechpartners fehlt.');
  if (!profile.bank.iban.trim()) issues.push('IBAN fehlt – ohne Zahlungskonto keine E-Rechnung.');
  else if (!isValidIban(profile.bank.iban)) issues.push(`IBAN „${profile.bank.iban}" ist nicht prüfziffernrichtig.`);
  return issues;
}

/** Reicht für den EXTF-Import: das Mandat beim Steuerberater. */
export function datevIssues(profile: TenantCompanyProfile): readonly string[] {
  const issues: string[] = [];
  const { consultantNumber, clientNumber, fiscalYearStart, accountLength } = profile.datev;
  if (!/^\d{1,7}$/.test(consultantNumber)) issues.push('Beraternummer fehlt (Ziffern, vom Steuerberater).');
  if (!/^\d{1,5}$/.test(clientNumber)) issues.push('Mandantennummer fehlt (Ziffern, vom Steuerberater).');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(fiscalYearStart)) issues.push('Beginn des Wirtschaftsjahres fehlt.');
  if (![4, 5, 6, 7, 8].includes(accountLength)) issues.push('Länge der Sachkonten ist ungültig (4–8).');
  return issues;
}

export function companyCapabilities(profile: TenantCompanyProfile): readonly CapabilityStatus[] {
  const letterhead = letterheadIssues(profile);
  const eInvoice = eInvoiceIssues(profile);
  const datev = datevIssues(profile);
  return [
    { capability: 'letterhead', label: 'Briefbogen & Belege', ready: letterhead.length === 0, issues: letterhead },
    { capability: 'eInvoice', label: 'E-Rechnung (XRechnung)', ready: eInvoice.length === 0, issues: eInvoice },
    { capability: 'datevExtf', label: 'DATEV EXTF-Export', ready: datev.length === 0, issues: datev },
  ];
}

export function isCapabilityReady(profile: TenantCompanyProfile, capability: CompanyCapability): boolean {
  return companyCapabilities(profile).find((entry) => entry.capability === capability)?.ready ?? false;
}

/** Die Fußzeilen-Pflichtangaben, aus der Identität abgeleitet — Vorschlag, überschreibbar. */
export function suggestedFooterColumns(profile: TenantCompanyProfile): readonly string[] {
  const { identity, contact, bank } = profile;
  return [
    [identity.name, identity.address.street, `${identity.address.postalCode} ${identity.address.city}`.trim()]
      .filter(Boolean).join('\n'),
    [contact.phone && `Tel. ${contact.phone}`, contact.email, contact.website].filter(Boolean).join('\n'),
    [bank.bankName, bank.iban && `IBAN ${bank.iban}`, bank.bic && `BIC ${bank.bic}`].filter(Boolean).join('\n'),
    [
      identity.registerCourt && identity.register ? `${identity.registerCourt} ${identity.register}` : identity.register,
      identity.managingDirectors && `GF: ${identity.managingDirectors}`,
      identity.vatId && `USt-IdNr. ${identity.vatId}`,
    ].filter(Boolean).join('\n'),
  ];
}

export interface CompanyProfileGateway {
  loadProfile(tenantId: string): Promise<TenantCompanyProfile>;
  saveProfile(tenantId: string, profile: TenantCompanyProfile): Promise<TenantCompanyProfile>;
}

/** Tiefes Auffüllen — ein gespeichertes Profil aus einer älteren Version darf nichts umwerfen. */
export function normalizeCompanyProfile(input: unknown): TenantCompanyProfile {
  const raw = (input ?? {}) as Partial<TenantCompanyProfile>;
  return {
    identity: {
      ...emptyCompanyProfile.identity,
      ...raw.identity,
      address: { ...emptyCompanyProfile.identity.address, ...raw.identity?.address },
    },
    contact: { ...emptyCompanyProfile.contact, ...raw.contact },
    bank: { ...emptyCompanyProfile.bank, ...raw.bank },
    letterhead: {
      ...emptyCompanyProfile.letterhead,
      ...raw.letterhead,
      footerColumns: raw.letterhead?.footerColumns ?? emptyCompanyProfile.letterhead.footerColumns,
    },
    documentDefaults: { ...emptyCompanyProfile.documentDefaults, ...raw.documentDefaults },
    datev: { ...emptyCompanyProfile.datev, ...raw.datev },
    dunning: {
      ...emptyCompanyProfile.dunning,
      ...raw.dunning,
      // Eine leere Stufenliste waere ein Mahnwesen ohne Mahnungen -- dann lieber die Vorgabe.
      levels: raw.dunning?.levels?.length ? raw.dunning.levels : emptyCompanyProfile.dunning.levels,
    },
  };
}
